import type {
  InjectionProtocol,
  ProtocolBrand,
  ProtocolCopy,
  ProtocolLocale,
} from "./types";

/**
 * Which name is on the box in front of her, and how it reaches the copy.
 *
 * The trade name is a market fact. The same choriogonadotropin alfa is Ovidrel
 * in the US, Canada and Australia and Ovitrelle in the EU, the UK and
 * Switzerland, and a patient reads whichever one her pharmacy dispensed.
 *
 * The protocol used to carry the brand inside its locale strings — English said
 * Ovidrel, French said Ovitrelle — which quietly asserts that everyone reading
 * French holds a French-market box. A francophone patient in Montreal does not:
 * Canada dispenses Ovidrel. She would have been told, at the moment she is
 * being asked to confirm what she is holding, that it is a product whose name
 * does not appear anywhere on it.
 *
 * The fix is not a better guess at her market. It is to stop guessing: her
 * prescription already carries the name, because that is what the clinic
 * prescribed and the pharmacy filled. `injectionCoachEligibility` matches the
 * dose by name before anything else happens, so the answer is already in hand —
 * it was simply being thrown away once the match succeeded.
 */

/** Everything a rendered step needs beyond the protocol itself. */
export type ProtocolCopyContext = {
  locale: ProtocolLocale;
  /** The trade name to speak. Never optional — see `renderProtocolCopy`. */
  brandName: string;
  /**
   * The dose her own prescription records, as she would read it on her schedule.
   *
   * Optional, and absent means the step says nothing about a number rather than
   * falling back to one. A step that told her the syringe holds a dose we cannot
   * source from her record would be asserting something at the exact moment she
   * is being asked to check it.
   */
  prescribedDose?: string | null;
};

/**
 * The brand the patient's own prescription names.
 *
 * Matched on the medication name as the calendar carries it, which is the same
 * string the eligibility check already matched to decide this dose is coachable
 * at all. Longest name first so that a brand which is a prefix of another —
 * none today, but the catalogue has `Gonal-f` and `Gonal-f RFF` waiting — cannot
 * shadow the more specific one.
 */
/**
 * Lowercased and stripped of accents, so a name matches however it is typed.
 *
 * Records reach this from clinics, imports and her own typing, and "Cétrorélix"
 * and "Cetrorelix" are the same prescription. Nothing in the catalogue is
 * distinguished by an accent, so folding can only make a match that should have
 * happened happen.
 */
function foldForMatching(text: string): string {
  return text
    .normalize("NFD")
    .replaceAll(/\p{Diacritic}/gu, "")
    .trim()
    .toLowerCase();
}

export function brandFromPrescribedName(
  protocol: InjectionProtocol,
  prescribedName: string,
): ProtocolBrand | null {
  const normalized = foldForMatching(prescribedName);
  if (!normalized) return null;

  const byLength = [...protocol.brands].sort(
    (a, b) => b.name.length - a.name.length,
  );
  return (
    byLength.find((brand) =>
      normalized.includes(foldForMatching(brand.name)),
    ) ?? null
  );
}

/**
 * The molecule name her record carries, when no trade name is in it.
 *
 * Separate from the brand lookup and deliberately second: a record naming a
 * product always wins, because that is the name on the box. This is the fallback
 * for a prescription written as the molecule — "Cetrorelix", "Ganirelix",
 * "Choriogonadotropin alfa" — which is how a good many clinics record one.
 */
export function aliasFromPrescribedName(
  protocol: InjectionProtocol,
  prescribedName: string,
): string | null {
  const normalized = foldForMatching(prescribedName);
  if (!normalized) return null;

  const byLength = [...(protocol.aliases ?? [])].sort(
    (a, b) => b.length - a.length,
  );
  return (
    byLength.find((alias) => normalized.includes(foldForMatching(alias))) ??
    null
  );
}

/**
 * What the coach may call this medication, given what her record calls it.
 *
 * The trade name when her record carries one, the molecule name when it carries
 * that instead, and null when it carries neither — in which case the coach says
 * nothing at all rather than naming a product it cannot source from her
 * prescription.
 *
 * It never invents. A patient prescribed "Cetrorelix" is told "Cetrorelix",
 * because which kit is on her table is a fact nobody here has.
 */
export function coachNameFromPrescribedName(
  protocol: InjectionProtocol,
  prescribedName: string,
): string | null {
  return (
    brandFromPrescribedName(protocol, prescribedName)?.name ??
    aliasFromPrescribedName(protocol, prescribedName)
  );
}

/**
 * Patient-facing text with the trade name filled in.
 *
 * `brandName` is required rather than optional on purpose. A protocol step that
 * rendered with an unresolved `{brand}` would put a template token in front of a
 * patient holding a needle, so the type makes that unreachable: callers must
 * resolve the brand first, and `resolveInjectionCoach` returns no candidate at
 * all when it cannot. Silence is the safe failure here, exactly as it is for an
 * unidentified device.
 */
export function renderProtocolCopy(
  copy: ProtocolCopy,
  { locale, brandName, prescribedDose }: ProtocolCopyContext,
): string {
  const text = locale === "fr" ? copy.fr : copy.en;
  // `{dose}` carries its own parentheses in the substitution rather than in the
  // copy, so an unknown dose leaves no empty brackets behind — the sentence
  // simply stops naming a number.
  const dose = prescribedDose?.trim();
  return text
    .replaceAll("{brand}", brandName)
    .replaceAll("{dose}", dose ? ` (${dose})` : "");
}
